import { USERS_COMBOBOX_QUERY } from '@/modules/users/queries'
import type { DropdownFilter } from '@/types/graphql'
import type { useLeaderGroupsSuspense } from '@/utils'
import { useUser } from '@/utils'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ResultOf } from 'gql.tada'
import type { ComboboxCommonProps, ComboboxItem } from './combobox'
import { Combobox } from './combobox'
import { getUserLabel } from '@/modules/users/util'

type UserNode = ResultOf<typeof USERS_COMBOBOX_QUERY>['allUsersList'][number]

interface CommonProps extends ComboboxCommonProps<string> {
  alwaysInclude?: Omit<UserNode, '__typename'> | null
  exclude?: string[]
  dropdownPageName?: DropdownFilter
  filterSalaryResponsible?: boolean
  filterLeaderGroup?: boolean
  hideItem?(item: UserNode, idx: number): boolean
}

interface SingleProps extends CommonProps {
  mode: 'single'
  value: string
  onChange(value: string): void
}

interface MultiProps extends CommonProps {
  mode: 'multiple'
  value: string[]
  onChange(value: string[]): void
}

type Props = SingleProps | MultiProps

export function UserCombobox({
  mode,
  value,

  alwaysInclude = null,
  exclude = [],
  dropdownPageName,
  filterSalaryResponsible = false,
  filterLeaderGroup = false,

  hideItem,

  placeholder,
  searchPlaceholder,
  noResultsText,

  onChange,

  ...props
}: Props) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.user.placeholder',
      searchPlaceholder: 'components.combobox.user.search-placeholder',
      noResults: 'components.combobox.user.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.user.placeholder',
      searchPlaceholder: 'components.multi-combobox.user.search-placeholder',
      noResults: 'components.multi-combobox.user.no-results',
    },
  })

  const me = useUser()

  const { data: usersData } = useQuery(USERS_COMBOBOX_QUERY, {
    variables: {
      includeSuperuser: me.isSuperuser,
      dropdown: dropdownPageName,
      filterLeaderGroup,
      filterSalaryResponsible,
    },
  })

  const userItems: ComboboxItem[] = (() => {
    const users = usersData?.allUsersList ?? []

    const items = users.reduce<ComboboxItem[]>((acc, user, idx) => {
      if (!!hideItem?.(user, idx)) return acc
      if (user.id === alwaysInclude?.id) return acc
      if (exclude.includes(user.id)) return acc

      const label = getUserLabel(user)

      acc.push({
        value: user.id,
        label: label || user.email,
        filterValue: user.internalNumber + ' ' + (user.fullName || user.email),
      })
      return acc
    }, [])

    if (!!alwaysInclude)
      items.unshift({
        value: alwaysInclude.id,
        label: alwaysInclude.fullName || alwaysInclude.email,
        filterValue:
          alwaysInclude.internalNumber +
          ' ' +
          (alwaysInclude.fullName || alwaysInclude.email),
      })

    return items
  })()

  if (mode === 'single')
    return (
      <Combobox
        {...props}
        mode="single"
        value={value}
        items={userItems}
        clearable
        placeholder={placeholder ?? translations.single.placeholder}
        searchPlaceholder={
          searchPlaceholder ?? translations.single.searchPlaceholder
        }
        noResultsText={noResultsText ?? translations.single.noResults}
        onChange={onChange}
      />
    )

  return (
    <Combobox
      {...props}
      mode="multiple"
      value={value}
      items={userItems}
      placeholder={placeholder ?? translations.multiple.placeholder}
      searchPlaceholder={
        searchPlaceholder ?? translations.multiple.searchPlaceholder
      }
      noResultsText={noResultsText ?? translations.multiple.noResults}
      onChange={onChange}
    />
  )
}

export function useHideBasedOnLeaderGroup(
  selectedLeaderGroupId: string,
  leaderGroups: ReturnType<typeof useLeaderGroupsSuspense>
) {
  const selectedLeaderGroup = leaderGroups.myLeaderGroups.find(
    lg => lg.id === selectedLeaderGroupId
  )
  const selectedLeaderGroupUsers = new Set(
    selectedLeaderGroup?.users.map(u => u.id) ?? []
  )

  return (item: { id: string }) => {
    if (!selectedLeaderGroup) return false
    return !selectedLeaderGroupUsers.has(item.id)
  }
}
